import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash-es';
import ActivityIcon from 'components/ActivityIcon';
import I18n from 'utils/I18n';
import styles from './styles.scss';
import ActivityEntryBody from '../shared/ActivityEntryBody';
import MaharajAchievementCelebration from '../shared/MaharajAchievementCelebration';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import KudosAndComments from '../shared/KudosAndComments';
import {
  segAndBestEffortAchievementsProps,
  cumulativeGoalAchievementProps,
  kudosAndCommentsProps,
  feedTypeProps,
  mapProps,
  pageProps,
  photoListProps,
  powerAndSegmentGoalAchievementsProps,
  statsProps,
  viewingAthleteProps,
  cursorDataProps,
  indexProps,
  entityAthleteProps,
  timeAndLocationProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';
import CommentDeleteAlertDialog from '../shared/CommentDeleteAlertDialog';
import { buildInitialState } from '../../state/feed-entity-init-state';
import EntryHeader from '../shared/EntryHeader/EntryHeader';
import VisibilityMessage from '../shared/VisibilityMessage';

const Activity = ({
  index,
  entity,
  activity,
  cursorData,
  page,
  feedType,
  viewingAthlete,
  clubId,
  athleteProfileId
}) => {
  const {
    avatarUrl,
    athleteName,
    athleteId,
    memberType,
    sex,
    firstName
  } = activity.athlete;

  const {
    activityName,
    id: activityId,
    flagged,
    ownedByCurrentAthlete,
    segAndBestEffortAchievements,
    powerAndSegmentGoalAchievements,
    cumulativeGoalAchievement,
    type: activityType,
    stats,
    description,
    maharajAchievement,
    isVirtual,
    isCommute,
    workoutType,
    shareable,
    twitterUrl,
    isBoosted,
    visibility,
    mapAndPhotos
  } = activity;

  const analyticProps = {
    page,
    entity,
    entityId: activityId,
    athleteProfileId,
    feedType,
    cursorData,
    viewingAthleteId: viewingAthlete.id,
    clubId
  };

  const { trackAnalytics, intersectionObserverRef } = useFeedAnalytics({
    ...analyticProps
  });

  const [entityState, dispatch] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics,
      comments: { [activityId]: activity.kudosAndComments.comments }
    }),
    init
  );
  const providerState = {
    entityState,
    dispatch,
    analyticProps
  };
  const cardMargin = index > 0 ? styles.cardMargin : '';
  const translatedActivityType = I18n.t(
    `strava.activities.types_v2.${activityType}`
  );

  const buildEntryBody = () => {
    const elements = [];
    // activity icon
    elements.push(
      <div
        key="activity_icon"
        className={`${styles.entryIcon} ${styles.mediaLeft}`}
      >
        <ActivityIcon
          type={activityType}
          size="small"
          className={styles.activityIconImage}
          title={translatedActivityType}
        />
      </div>
    );
    // activity title, description, stats and acheivements
    elements.push(
      <div key="activity_entry_body" className={`${styles.mediaBody}`}>
        <ActivityEntryBody
          stats={stats}
          description={description}
          flagged={flagged}
          activityName={activityName}
          activityId={activityId}
          ownedByCurrentAthlete={ownedByCurrentAthlete}
          segAndBestEffortAchievements={segAndBestEffortAchievements}
          powerAndSegmentGoalAchievements={powerAndSegmentGoalAchievements}
          cumulativeGoalAchievement={cumulativeGoalAchievement}
          activityType={activityType}
          athleteSex={sex}
          athleteFirstName={firstName}
        />
      </div>
    );
    return elements;
  };

  const entityOwner = {
    displayImage: avatarUrl,
    name: athleteName,
    memberType,
    profileUrl: `/athletes/${athleteId}`,
    type: 'athlete'
  };

  const buildEntryFooter = () => (
    <div className={styles.entryFooter}>
      {ownedByCurrentAthlete && visibility !== 'everyone' && (
        <VisibilityMessage visibility={visibility} />
      )}
      <KudosAndComments
        kudosAndComments={activity.kudosAndComments}
        entity={entity}
        entityId={activityId}
        ownedByCurrentAthlete={ownedByCurrentAthlete}
        viewingAthlete={viewingAthlete}
        entityOwner={entityOwner}
        entityTitle={activityName}
        openCommentsModalOnIconClick={false}
        segAndBestEffortAchievements={segAndBestEffortAchievements}
        showHairlineOnTop={
          (isEmpty(mapAndPhotos) && isEmpty(maharajAchievement)) ||
          (ownedByCurrentAthlete && visibility !== 'everyone')
        }
      />
    </div>
  );

  return (
    <FeedEntityContext.Provider value={providerState}>
      {/* web-feed-4-entry is being used by feed3 container for pagination - https://github.com/strava/active/blob/main/app/assets/javascripts/strava/dashboard/PaginationView.js.coffee#L52-L73 */}
      <div
        className={`web-feed-4-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
        data-updated-at={cursorData.updated_at}
        data-rank={cursorData.rank}
        data-testid="web-feed-entry"
        ref={intersectionObserverRef}
      >
        {/* Entry Header - avatar, location, social actions */}
        <EntryHeader
          timeAndLocation={activity.timeAndLocation}
          entityOwner={entityOwner}
          entityId={activityId}
          isBoosted={isBoosted || false}
          shareable={shareable || false}
          twitterUrl={twitterUrl}
        />

        {/* Entry Body =  activity title, description, stats and achievements  */}
        <div className={styles.entryBody}>
          <div className={`${styles.media}`}>{buildEntryBody()}</div>
        </div>

        {/* Maharaj achievements */}
        {!isEmpty(maharajAchievement) && (
          <div className={styles.entryMedia}>
            <MaharajAchievementCelebration
              image={maharajAchievement.image}
              image_caption={maharajAchievement.image_caption}
              subtitle={maharajAchievement.subtitle}
              text={maharajAchievement.text}
              url={maharajAchievement.url}
            />
          </div>
        )}

        {/* Map and Photos */}
        {!isEmpty(mapAndPhotos) && (
          <div className={styles.entryMedia}>
            <PhotosAndMapImage
              activityId={activityId}
              activityMap={mapAndPhotos.activityMap}
              entryType={entity.toLowerCase()}
              isRoutable={mapAndPhotos.isRoutable}
              photoList={mapAndPhotos.photoList}
              feedName={page}
              isVirtual={isVirtual}
              isCommute={isCommute}
              workoutType={workoutType}
            />
          </div>
        )}

        {/* Entry Footer - Visibility Message , Kudos & Comments */}
        {buildEntryFooter()}
        <CommentDeleteAlertDialog />
      </div>
    </FeedEntityContext.Provider>
  );
};

Activity.propTypes = {
  // feed entry types could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Activity']).isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  activity: PropTypes.shape({
    activityName: PropTypes.string,
    id: PropTypes.string,
    flagged: PropTypes.bool,
    ownedByCurrentAthlete: PropTypes.bool,
    segAndBestEffortAchievements: segAndBestEffortAchievementsProps,
    powerAndSegmentGoalAchievements: powerAndSegmentGoalAchievementsProps,
    cumulativeGoalAchievement: cumulativeGoalAchievementProps,
    type: PropTypes.string,
    stats: statsProps,
    description: PropTypes.string,
    maharajAchievement: PropTypes.shape({
      image: PropTypes.string,
      image_caption: PropTypes.string,
      subtitle: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string
    }),
    isVirtual: PropTypes.bool.isRequired,
    isCommute: PropTypes.bool.isRequired,
    workoutType: PropTypes.string,
    shareable: PropTypes.bool.isRequired,
    twitterUrl: PropTypes.string.isRequired,
    isBoosted: PropTypes.bool,
    visibility: PropTypes.string.isRequired,
    athlete: entityAthleteProps.isRequired,
    mapAndPhotos: PropTypes.shape({
      activityMap: mapProps,
      isRoutable: PropTypes.bool,
      photoList: photoListProps
    }).isRequired,
    timeAndLocation: timeAndLocationProps.isRequired,
    kudosAndComments: kudosAndCommentsProps.isRequired
  }).isRequired,
  cursorData: cursorDataProps.isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  athleteProfileId: PropTypes.string.isRequired
};

export default Activity;
